import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Search, CheckCircle, FileText, AlertTriangle } from 'lucide-react';
import { statementsApi, previewUrl } from '../services/api';
import { AuthedLink } from './AuthedFile';
import { Btn, Input, Alert, Modal, Badge, QueryState, ConfirmDialog } from './UI';
import { fmtMoney, fmtDateHu } from '../utils';
import { usePostHog } from '@posthog/react';

// ⚠ 2026-09-16: TOKENEK, nem hard-kódolt hexek. A korábbi `'dátum': '#64748b'`
// pontosan az az érték volt, amit az `index.css:21` 3,53:1 kontraszttal
// KIVEZETETT — 0,62rem méretben renderelve. A hard-kódolt `--primary` ráadásul a
// Raymond white-label skint is megkerülte (`index.css:83`: #fee600).
const REASON_COLORS = {
  'közlemény':  'var(--accent)',      // számlaszám a közleményben — önazonosító
  'közlemény~': 'var(--warning)',     // leíró címke ('osztalék') — NEM azonosító
  'összeg':     'var(--accent)',
  'IBAN':       'var(--primary)',
  'név':        'var(--primary)',
  'dátum':      'var(--text-muted)',
  'dátum~':     'var(--text-muted)',  // gyengébb jel — a szaggatott keret különbözteti meg
};
const WEAK_REASONS = new Set(['dátum~', 'közlemény~']);

// A backend `band()`-je — a felület ezt írja ki, nem a lista sorszámát.
const BAND_LABEL = {
  auto:   { text: 'biztos egyezés', color: 'var(--accent)' },
  review: { text: 'átnézendő',      color: 'var(--warning)' },
};

// Egy tranzakcióhoz bizonylat keresése + párosítása: auto-jelöltek a DB-ből
// (összeg / IBAN / név / közlemény / dátum), plusz kézi számlaszám-keresés.
export default function TxMatchModal({ open, tx, onClose, onMatched }) {
  const posthog = usePostHog();
  const [term, setTerm] = useState('');       // beküldött keresőkifejezés
  const [input, setInput] = useState('');      // aktuális input
  const [learned, setLearned] = useState(null); // tanult alias a párosításból
  const [confirming, setConfirming] = useState(null); // megerősítésre váró jelölt

  const txId = tx?.id;

  // ⚠ 2026-09-16 (G12): a komponens VÉGIG mountolva marad (a hívóhelyek csak az
  // `open`-t váltják), és eddig SEMMI nem nullázta a state-et. Ezért a következő
  // tranzakció megnyitásakor az előző „aliast tanult" sikerképernyője, vagy egy
  // bennragadt keresőszó fogadta a felhasználót — és a query key a RÉGI szóval
  // kért adatot az ÚJ tranzakcióhoz.
  useEffect(() => {
    if (open) { setTerm(''); setInput(''); setLearned(null); setConfirming(null); }
  }, [open, txId]);

  const candQuery = useQuery({
    queryKey: ['tx-match-candidates', txId, term],
    queryFn: () => statementsApi.matchCandidates(txId, term || undefined),
    enabled: open && !!txId,
  });
  const data = candQuery.data;
  const candidates = data?.candidates || [];
  const totalCandidates = data?.total_candidates ?? candidates.length;
  const gateDays = data?.date_gate_days ?? 0;

  // ── Döntést segítő összevetés a TRANZAKCIÓVAL ──────────────────────────────
  const txAmt = tx?.amount, txCur = tx?.currency || 'HUF', txDate = tx?.tx_date;
  const amtOf = (c) => (c.payable_amount != null ? c.payable_amount : c.gross_amount);

  const amountVerdict = (c) => {
    const a = amtOf(c);
    if (a == null || txAmt == null) return null;
    // ⚠ A bizonylat összege NEGATÍV is lehet (jóváíró számla), a tx.amount mindig
    // pozitív — az irányt a tx_type hordozza. Ezért abszolút értéken vetünk össze,
    // ugyanúgy, mint a backend `_amount_matches`-e.
    const absA = Math.abs(a);
    // FX: a tranzakció másik oldala is egyezhet (HUF terhelés ↔ USD számla).
    const fxSame = tx?.fx_counter_currency && tx.fx_counter_currency === (c.currency || 'HUF');
    if ((c.currency || 'HUF') !== txCur && !fxSame) return { kind: 'warn', text: 'más deviza' };
    const cmpAmt = fxSame ? Math.abs(tx.fx_counter_amount ?? 0) : Math.abs(txAmt);
    const cmpCur = fxSame ? tx.fx_counter_currency : txCur;
    const tol = cmpCur === 'HUF' ? 1 : 0.02;
    const d = absA - cmpAmt;
    if (Math.abs(d) <= Math.max(tol, absA * 0.001)) {
      // ⚠ F20: a 0,1%-os relatív tűrés nagy összegnél sok pénz. Ha nem PONTOS az
      // egyezés, mondjuk ki, mennyi a maradék — ne állítsunk többet, mint amennyi igaz.
      return Math.abs(d) < 0.005
        ? { kind: 'match', text: '= tétel' }
        : { kind: 'match', text: `= tétel (±${fmtMoney(Math.abs(d), cmpCur)})` };
    }
    return { kind: 'warn', text: `${d > 0 ? '+' : '−'}${fmtMoney(Math.abs(d), cmpCur)} eltérés` };
  };

  const dateInfo = (c) => {
    const raw = c.due_date || c.delivery_date || c.issue_date;
    if (!raw) return null;
    const lbl = c.due_date ? 'fiz. hat.' : c.delivery_date ? 'teljesítés' : 'kiállítás';
    let days = c.date_distance_days ?? null;
    if (days == null && txDate) days = Math.round(Math.abs(new Date(raw) - new Date(txDate)) / 86400000);
    const signed = txDate ? Math.round((new Date(raw) - new Date(txDate)) / 86400000) : null;
    return { lbl, raw, days, signed };
  };
  const dayText = (days) => days == null ? '' :
    days === 0 ? 'a tétel napján' :
    Math.abs(days) <= 31 ? `${Math.abs(days)} nap ${days > 0 ? 'később' : 'korábban'}` :
    `${Math.round(Math.abs(days) / 30)} hó ${days > 0 ? 'később' : 'korábban'}`;

  // ⚠ G9: kimenő bizonylaton a `supplier_name` A MI CÉGÜNK (billingo_import.py:374).
  // A `supplier_name || customer_name` ezért kimenő iránynál MINDEN jelöltre
  // ugyanazt a nevet írta ki — a vevő neve, az egyetlen megkülönböztető adat,
  // sosem látszott. Az irány a válaszban benne van, csak nem használtuk.
  const partnerName = (c) => {
    const outgoing = String(c.direction || '').toLowerCase() === 'outgoing';
    return (outgoing ? (c.customer_name || c.supplier_name)
                     : (c.supplier_name || c.customer_name)) || '–';
  };

  const matchMut = useMutation({
    mutationFn: (docId) => statementsApi.match(txId, docId),
    onSuccess: (data) => {
      posthog?.capture('bank_statement_transaction_matched', {
        alias_learned: !!data?.alias_learned,
        // ⚠ Csak DARABSZÁM — összeg és IBAN soha (`.claude/rules/posthog-analitika.md`).
        gate_blocked_candidates: candidates.filter(c => c.band === 'review'
          && gateDays && c.date_distance_days != null
          && c.date_distance_days > gateDays).length,
      });
      onMatched?.();
      // Ha a párosításból aliast tanult a rendszer, mutassuk meg (ne zárjunk azonnal).
      if (data?.alias_learned) setLearned(data.alias_learned);
      else onClose?.();
    },
  });

  const runSearch = () => setTerm(input.trim());
  const attach = (c) => {
    // ⚠ G8/C9: ha a bizonylat MÁS tranzakcióhoz már csatolva van, kérjünk tudatos
    // megerősítést. Nem tiltjuk — a részletfizetésnél ez teljesen jogos.
    if ((c.linked_tx_ids || []).length) setConfirming(c);
    else matchMut.mutate(c.id);
  };

  if (learned) {
    return (
      <Modal open={open} onClose={onClose} title="Párosítva" width={520}>
        <Alert type="success">
          Párosítva ✓ — és a rendszer <strong>aliast tanult</strong>:{' '}
          „{learned.alias}" → „{learned.partner_name}". A jövőben az ilyen nevű
          tételek automatikusan párosulnak ehhez a partnerhez.
        </Alert>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.9rem' }}>
          <Btn onClick={() => { setLearned(null); onClose?.(); }}>Bezárás</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Bizonylat keresése a tranzakcióhoz" width={640}>
      {tx && (
        <div style={{ background: 'var(--bg-input)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius)', padding: '0.6rem 0.75rem', marginBottom: '0.9rem',
          fontSize: '0.8rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
            <span style={{ fontWeight: 600 }}>{tx.counterparty_name || '– ismeretlen partner –'}</span>
            <span style={{ fontFamily: 'var(--mono)', fontWeight: 700,
              color: String(tx.tx_type).toLowerCase() === 'credit' ? 'var(--accent)' : 'inherit' }}>
              {String(tx.tx_type).toLowerCase() === 'credit' ? '+' : '−'}{fmtMoney(tx.amount, tx.currency)}
            </span>
          </div>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.72rem', marginTop: 2 }}>
            {fmtDateHu(tx.tx_date)} · {tx.reference || 'nincs közlemény'}
            {/* ⚠ F9: az FX-tranzakció MÁSIK oldala eddig eltűnt a fejlécből, pedig a
                párosítás azon is egyezhet — és a kártya emiatt mondott önmagának
                ellentmondó dolgot ('összeg' zöld + 'más deviza' narancs). */}
            {tx.fx_counter_amount != null && tx.fx_counter_currency && (
              <> · <span style={{ fontFamily: 'var(--mono)' }}>
                {fmtMoney(tx.fx_counter_amount, tx.fx_counter_currency)}
              </span> devizában</>
            )}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
        <div style={{ flex: 1 }}>
          <Input placeholder="Számlaszám vagy szállító (ha nem a jelöltek közül választasz)…"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') runSearch(); }} />
        </div>
        <Btn icon={Search} onClick={runSearch}>Keresés</Btn>
        {term && (
          <Btn variant="secondary" onClick={() => { setInput(''); setTerm(''); }}>Vissza a jelöltekhez</Btn>
        )}
      </div>

      {matchMut.isError && (
        <Alert type="danger" style={{ marginBottom: '0.75rem' }}>
          {matchMut.error?.response?.data?.detail || 'Párosítási hiba'}
        </Alert>
      )}

      {/* ⚠⚠ G10/INV-13: eddig CSAK az `isLoading` volt elágaztatva. Egy 500-as vagy
          hálózati hiba esetén a `data` undefined lett, a lista üres, és a felhasználó
          egy megnyugtató kék dobozt kapott: „Nincs automatikus jelölt." — vagyis azt
          állítottuk, nincs mit párosítani, miközben meg sem kérdeztük. A `QueryState`
          pontosan erre készült (UI.js:308), és nem volt használva.
          ⚠ A gyerek FÜGGVÉNYKÉNT megy át: eagerly értékelődne, és `data`-t olvas. */}
      <QueryState
        query={candQuery}
        empty={candidates.length === 0}
        emptyText={term ? `Nincs találat erre: „${term}".`
                        : 'Nincs automatikus jelölt. Add meg a számlaszámot a keresőben.'}
        errorText="A jelöltek betöltése nem sikerült."
      >
        {() => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
            {!term && (
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Legjobb egyezések — vesd össze a tétellel (összeg, dátum):
              </div>
            )}
            {candidates.map((c) => {
              const av = amountVerdict(c);
              const di = dateInfo(c);
              const matching = matchMut.isPending && matchMut.variables === c.id;
              const accent = av?.kind === 'match' ? 'var(--accent)' : 'var(--warning)';
              const bandInfo = BAND_LABEL[c.band];
              const gateBlocked = gateDays && c.band === 'review'
                && c.date_distance_days != null && c.date_distance_days > gateDays;
              const alsoLinked = (c.linked_tx_ids || []).length;
              return (
                <div key={c.id} style={{
                  border: '1px solid var(--border)',
                  borderLeft: c.band === 'auto' ? '3px solid var(--accent)' : '1px solid var(--border)',
                  borderRadius: 'var(--radius)', padding: '0.6rem 0.75rem',
                  display: 'flex', alignItems: 'flex-start', gap: 12, background: 'var(--bg-card)' }}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>{partnerName(c)}</span>
                      {c.invoice_number && (
                        <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                          {c.invoice_number}
                        </span>
                      )}
                      {/* ⚠ G6: a bizonylat AZONOSÍTÓJA — enélkül két, egy karakterben
                          eltérő számlaszámú duplikátum megkülönböztethetetlen volt. */}
                      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem',
                        color: 'var(--text-muted)' }}>#{c.id}</span>
                      {/* ⚠ F6: a PDF eddig statikus ikon volt, nem link — pedig pont ez
                          dönti el, melyik a helyes bizonylat. */}
                      {c.has_pdf && (
                        <AuthedLink href={previewUrl(c.id)}
                          title="PDF megnyitása" style={{ display: 'inline-flex', color: 'var(--accent)' }}>
                          <FileText size={12} />
                        </AuthedLink>
                      )}
                      {/* ⚠⚠ G13: eddig a lista ELSŐ eleme kapta a „LEGJOBB EGYEZÉS"
                          bélyeget, bármilyen gyenge is volt — egy 8 pontos jelölt
                          ugyanazt, mint egy 160 pontos. Most a backend `band()`-je
                          dönt, és a küszöb alatt semmi nem jelenik meg. */}
                      {bandInfo && (
                        <span style={{ fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.04em',
                          textTransform: 'uppercase', color: bandInfo.color,
                          border: `1px solid ${bandInfo.color}`, borderRadius: 8, padding: '0 6px' }}>
                          {bandInfo.text}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5,
                      flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      {di && (
                        <span>{di.lbl}: <span style={{ color: 'var(--text)', fontFamily: 'var(--mono)' }}>
                          {fmtDateHu(di.raw)}</span>
                          {di.signed != null && <span> · {dayText(di.signed)}</span>}</span>
                      )}
                      {c.accounting_period && <span>· {c.accounting_period}</span>}
                      {c.status && <Badge variant={c.status}>{c.status}</Badge>}
                      {(c.match_reasons || []).map(r => (
                        <span key={r} style={{ fontSize: '0.62rem', fontWeight: 700, padding: '1px 6px',
                          borderRadius: 8, color: REASON_COLORS[r] || 'var(--text-muted)',
                          border: `1px ${WEAK_REASONS.has(r) ? 'dashed' : 'solid'} ${REASON_COLORS[r] || 'var(--border)'}` }}>{r}</span>
                      ))}
                    </div>
                    {/* ⚠ A felhasználónak látnia kell, MIÉRT nem társult automatikusan. */}
                    {gateBlocked ? (
                      <div style={{ marginTop: 5, fontSize: '0.68rem', color: 'var(--warning)' }}>
                        ⓘ Automatikusan nem társult: {c.date_distance_days} nap eltérés,
                        a határ {gateDays} nap.
                      </div>
                    ) : null}
                    {alsoLinked ? (
                      <div style={{ marginTop: 5, fontSize: '0.68rem', color: 'var(--warning)',
                        display: 'flex', alignItems: 'center', gap: 4 }}>
                        <AlertTriangle size={11} />
                        Már csatolva {alsoLinked === 1 ? 'egy másik tételhez' : `${alsoLinked} másik tételhez`}
                        {' '}(#{(c.linked_tx_ids || []).join(', #')})
                      </div>
                    ) : null}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontFamily: 'var(--mono)', fontWeight: 700, fontSize: '0.9rem',
                      fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {fmtMoney(amtOf(c), c.currency)}
                    </span>
                    {/* ⚠ F21: ha a fizetendő eltér a bruttótól (előleg-beszámítás),
                        mondjuk ki — eddig egy szám látszott, cimke nélkül. */}
                    {c.payable_amount != null && c.gross_amount != null
                      && Math.abs(c.payable_amount - c.gross_amount) > 0.005 && (
                      <span style={{ fontSize: '0.64rem', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                        fizetendő · bruttó {fmtMoney(c.gross_amount, c.currency)}
                      </span>
                    )}
                    {av && (
                      <span style={{ fontSize: '0.66rem', fontWeight: 700, padding: '1px 7px', borderRadius: 8,
                        color: accent, border: `1px solid ${accent}`, whiteSpace: 'nowrap' }}>
                        {av.kind === 'match' ? '✓ ' : ''}{av.text}
                      </span>
                    )}
                    {/* ⚠ F17: párosítás közben MINDEN gomb tiltva — eddig csak a
                        megnyomott pörgött, a többi kattintható maradt, így két
                        bizonylat is rálőhető volt ugyanarra a tranzakcióra. */}
                    <Btn size="sm" variant="outline" icon={CheckCircle} loading={matching}
                      disabled={matchMut.isPending && !matching}
                      onClick={() => attach(c)}>Csatol</Btn>
                  </div>
                </div>
              );
            })}
            {/* ⚠ F16: a lista 12 elemre van vágva — eddig ezt semmi nem jelezte,
                a felhasználó azt hihette, mindent látott. */}
            {totalCandidates > candidates.length && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', textAlign: 'center',
                padding: '0.3rem' }}>
                A legjobb {candidates.length} jelölt látszik a(z) {totalCandidates} találatból —
                szűkíts a keresővel.
              </div>
            )}
          </div>
        )}
      </QueryState>

      <ConfirmDialog
        open={!!confirming}
        title="Ez a bizonylat már csatolva van"
        danger
        message={confirming ? (
          <>
            A(z) <strong>#{confirming.id}</strong> bizonylat
            ({partnerName(confirming)}, {fmtMoney(amtOf(confirming), confirming.currency)})
            már csatolva van {(confirming.linked_tx_ids || []).length} másik banki tételhez
            (#{(confirming.linked_tx_ids || []).join(', #')}).
            <br /><br />
            Ez <strong>részletfizetésnél jogos</strong> — egy számlához több átutalás is
            tartozhat. Minden más esetben viszont azt jelenti, hogy a számlát kétszer
            számolnánk el. Biztosan csatolod?
          </>
        ) : null}
        confirmLabel="Igen, csatolom"
        loading={matchMut.isPending}
        onCancel={() => setConfirming(null)}
        onConfirm={() => { const c = confirming; setConfirming(null); matchMut.mutate(c.id); }}
      />
    </Modal>
  );
}
